import React, { useState } from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { siteConfig, wellnessDomains } from '../data/mock';
import { toast } from 'sonner';

const ApplyPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    age: '',
    occupation: '',
    referralSource: '',
    primaryGoals: '',
    priorityDomains: [],
    currentChallenges: '',
    previousCoaching: '',
    commitment: '',
    agreeTerms: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDomainToggle = (domain) => {
    setFormData((prev) => ({
      ...prev,
      priorityDomains: prev.priorityDomains.includes(domain)
        ? prev.priorityDomains.filter((d) => d !== domain)
        : [...prev.priorityDomains, domain],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast.success('Application submitted successfully! We\'ll be in touch within 24 hours.');
    setStep(4);
  };

  const nextStep = () => {
    if (step === 1 && (!formData.firstName || !formData.lastName || !formData.email)) {
      toast.error('Please fill in all required fields');
      return;
    }
    setStep((prev) => prev + 1);
  };

  const prevStep = () => setStep((prev) => prev - 1);

  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section with Logo */}
      <section className="py-16 bg-[#F8F9FA] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[700px] h-[700px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center max-w-2xl mx-auto bg-white/95 backdrop-blur-sm p-8 rounded-lg border-2 border-[#1E3A8A]">
            <h1 className="text-[#1E3A8A] mb-6">
              Start Your <span className="text-[#E31837]">Transformation</span>
            </h1>
            <p className="text-[#1E3A8A] text-xl">
              Complete this brief assessment to begin your 12-week wellness journey.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[900px] h-[900px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-2xl mx-auto">
            {/* Progress Steps */}
            <div className="flex items-center justify-center mb-16">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all border-2 ${
                      step >= s
                        ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]'
                        : 'bg-white text-[#1E3A8A] border-[#D1D9E6]'
                    }`}
                  >
                    {step > s ? <CheckCircle className="w-6 h-6" /> : s}
                  </div>
                  {s < 3 && (
                    <div
                      className={`w-24 h-1 mx-3 rounded transition-all ${
                        step > s ? 'bg-[#1E3A8A]' : 'bg-[#D1D9E6]'
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            {step < 4 ? (
              <Card className="card-bordered bg-white/95 backdrop-blur-sm">
                <CardHeader className="border-b-2 border-[#D1D9E6]">
                  <CardTitle className="text-[#1E3A8A] text-2xl">
                    {step === 1 && 'Personal Information'}
                    {step === 2 && 'Wellness Assessment'}
                    {step === 3 && 'Goals & Commitment'}
                  </CardTitle>
                  <CardDescription className="text-[#1E3A8A] text-base">
                    {step === 1 && 'Tell us a bit about yourself'}
                    {step === 2 && 'Help us understand your current wellness state'}
                    {step === 3 && 'Define your goals and commitment level'}
                  </CardDescription>
                </CardHeader>

                <CardContent className="p-8">
                  <form onSubmit={handleSubmit}>
                    {step === 1 && (
                      <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <Label htmlFor="firstName" className="text-[#1E3A8A] font-semibold">First Name *</Label>
                            <Input
                              id="firstName"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleInputChange}
                              className="mt-2 bg-white border-2 border-[#D1D9E6] text-[#1E3A8A] focus:border-[#1E3A8A]"
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="lastName" className="text-[#1E3A8A] font-semibold">Last Name *</Label>
                            <Input
                              id="lastName"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleInputChange}
                              className="mt-2 bg-white border-2 border-[#D1D9E6] text-[#1E3A8A] focus:border-[#1E3A8A]"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="email" className="text-[#1E3A8A] font-semibold">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="mt-2 bg-white border-2 border-[#D1D9E6] text-[#1E3A8A] focus:border-[#1E3A8A]"
                            required
                          />
                        </div>

                        <div>
                          <Label htmlFor="phone" className="text-[#1E3A8A] font-semibold">Phone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="mt-2 bg-white border-2 border-[#D1D9E6] text-[#1E3A8A] focus:border-[#1E3A8A]"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <Label htmlFor="age" className="text-[#1E3A8A] font-semibold">Age</Label>
                            <Input
                              id="age"
                              name="age"
                              type="number"
                              value={formData.age}
                              onChange={handleInputChange}
                              className="mt-2 bg-white border-2 border-[#D1D9E6] text-[#1E3A8A] focus:border-[#1E3A8A]"
                            />
                          </div>
                          <div>
                            <Label htmlFor="occupation" className="text-[#1E3A8A] font-semibold">Occupation</Label>
                            <Input
                              id="occupation"
                              name="occupation"
                              value={formData.occupation}
                              onChange={handleInputChange}
                              className="mt-2 bg-white border-2 border-[#D1D9E6] text-[#1E3A8A] focus:border-[#1E3A8A]"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-8">
                        <div>
                          <Label className="text-[#1E3A8A] font-semibold mb-4 block text-lg">Select your top 3 priority wellness domains:</Label>
                          <div className="grid grid-cols-2 gap-4">
                            {wellnessDomains.map((domain) => (
                              <button
                                key={domain.id}
                                type="button"
                                onClick={() => handleDomainToggle(domain.name)}
                                className={`p-5 rounded-lg border-2 text-left transition-all ${
                                  formData.priorityDomains.includes(domain.name)
                                    ? 'bg-[#1E3A8A] border-[#1E3A8A] text-white'
                                    : 'bg-white border-[#D1D9E6] text-[#1E3A8A] hover:border-[#1E3A8A]'
                                }`}
                              >
                                <span className="font-semibold text-lg">{domain.name}</span>
                                <p className={`text-sm mt-1 ${formData.priorityDomains.includes(domain.name) ? 'text-gray-300' : 'text-[#1E3A8A]'}`}>{domain.focusAreas}</p>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="currentChallenges" className="text-[#1E3A8A] font-semibold">
                            What are your current challenges?
                          </Label>
                          <Textarea
                            id="currentChallenges"
                            name="currentChallenges"
                            value={formData.currentChallenges}
                            onChange={handleInputChange}
                            className="mt-2 bg-white border-2 border-[#D1D9E6] text-[#1E3A8A] focus:border-[#1E3A8A] min-h-[120px]"
                            placeholder="Describe what you're struggling with..."
                          />
                        </div>

                        <div>
                          <Label className="text-[#1E3A8A] font-semibold mb-4 block">Have you worked with a coach before?</Label>
                          <RadioGroup
                            value={formData.previousCoaching}
                            onValueChange={(value) => setFormData((prev) => ({ ...prev, previousCoaching: value }))}
                            className="flex gap-6"
                          >
                            <div className="flex items-center space-x-3">
                              <RadioGroupItem value="yes" id="yes" className="border-2 border-[#1E3A8A]" />
                              <Label htmlFor="yes" className="text-[#1E3A8A] font-medium">Yes</Label>
                            </div>
                            <div className="flex items-center space-x-3">
                              <RadioGroupItem value="no" id="no" className="border-2 border-[#1E3A8A]" />
                              <Label htmlFor="no" className="text-[#1E3A8A] font-medium">No</Label>
                            </div>
                          </RadioGroup>
                        </div>
                      </div>
                    )}

                    {step === 3 && (
                      <div className="space-y-8">
                        <div>
                          <Label htmlFor="primaryGoals" className="text-[#1E3A8A] font-semibold">
                            What are your primary goals for this program?
                          </Label>
                          <Textarea
                            id="primaryGoals"
                            name="primaryGoals"
                            value={formData.primaryGoals}
                            onChange={handleInputChange}
                            className="mt-2 bg-white border-2 border-[#D1D9E6] text-[#1E3A8A] focus:border-[#1E3A8A] min-h-[120px]"
                            placeholder="What do you hope to achieve?"
                          />
                        </div>

                        <div>
                          <Label className="text-[#1E3A8A] font-semibold mb-4 block">How committed are you to this transformation?</Label>
                          <RadioGroup
                            value={formData.commitment}
                            onValueChange={(value) => setFormData((prev) => ({ ...prev, commitment: value }))}
                          >
                            <div className="space-y-4">
                              <div className="flex items-center space-x-4 card-bordered p-5 bg-white">
                                <RadioGroupItem value="ready" id="ready" className="border-2 border-[#1E3A8A]" />
                                <Label htmlFor="ready" className="text-[#1E3A8A] cursor-pointer flex-1">
                                  <span className="font-semibold text-lg">Ready to commit</span>
                                  <p className="text-[#1E3A8A]">I'm prepared to invest time and energy</p>
                                </Label>
                              </div>
                              <div className="flex items-center space-x-4 card-bordered p-5 bg-white">
                                <RadioGroupItem value="exploring" id="exploring" className="border-2 border-[#1E3A8A]" />
                                <Label htmlFor="exploring" className="text-[#1E3A8A] cursor-pointer flex-1">
                                  <span className="font-semibold text-lg">Still exploring</span>
                                  <p className="text-[#1E3A8A]">I want to learn more first</p>
                                </Label>
                              </div>
                            </div>
                          </RadioGroup>
                        </div>

                        <div className="flex items-start space-x-4 card-bordered p-5 bg-white">
                          <Checkbox
                            id="agreeTerms"
                            checked={formData.agreeTerms}
                            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, agreeTerms: checked }))}
                            className="border-2 border-[#1E3A8A] mt-1"
                          />
                          <Label htmlFor="agreeTerms" className="text-[#1E3A8A] cursor-pointer">
                            I agree to receive communications from Integrated Wellness and understand this assessment helps determine the best care path for me.
                          </Label>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between mt-10 pt-8 border-t-2 border-[#D1D9E6]">
                      {step > 1 ? (
                        <Button
                          type="button"
                          onClick={prevStep}
                          className="btn-secondary"
                        >
                          Back
                        </Button>
                      ) : (
                        <div />
                      )}

                      {step < 3 ? (
                        <Button type="button" onClick={nextStep} className="btn-primary">
                          Continue
                          <ArrowRight className="w-5 h-5" />
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          className="btn-primary"
                          disabled={!formData.agreeTerms}
                        >
                          Submit Application
                          <ArrowRight className="w-5 h-5" />
                        </Button>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className="card-bordered bg-white/95 backdrop-blur-sm text-center py-16">
                <CardContent>
                  <div className="w-24 h-24 rounded-full border-4 border-[#1E3A8A] flex items-center justify-center mx-auto mb-8">
                    <CheckCircle className="w-12 h-12 text-[#E31837]" />
                  </div>
                  <h2 className="text-[#1E3A8A] text-3xl font-bold mb-6">Application Received!</h2>
                  <p className="text-[#1E3A8A] text-lg mb-8 max-w-md mx-auto">
                    Thank you for taking the first step toward transformation. Our team will review your application and reach out within 24 hours.
                  </p>
                  <p className="text-[#1E3A8A]">
                    Questions? Call us at{' '}
                    <a href={`tel:${siteConfig.phone.replace(/[^0-9]/g, '')}`} className="text-[#E31837] font-semibold">
                      {siteConfig.phone}
                    </a>
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplyPage;
